import * as ft from 'funtypes'
// Type-only, so the model's enum can be reused here without a runtime cycle:
// `domain/types.ts` imports `ConnectorStandard` from this module.
import type { CurrentType } from './types.js'

/**
 * Canonical connector standards, plus the mapping from EIPA's
 * `dictionary.connector_interface` names onto them.
 *
 * EIPA identifies a connector by a *name* (`IEC-62196-T2-F-NOCABLE`) and a
 * numeric *id*. Only the name is stable: the ids are assigned by the registry
 * and can be renumbered. So the name -> standard table below is hardcoded, and
 * ids are resolved at runtime from the fetched `dictionary.json` via
 * {@link createConnectorStandardLookup}.
 *
 * The canonical set collapses EIPA's 30-odd rows into what a driver actually
 * chooses between. Notably every `DOMESTIC-*` variant folds into `DOMESTIC`:
 * the distinction between a type E and a type F socket is not something worth
 * filtering a map by.
 */

export const CONNECTOR_STANDARDS = [
  /** Any household socket: EIPA `DOMESTIC-A` .. `DOMESTIC-M`, plus NEMA 5-20. */
  'DOMESTIC',
  /** IEC 60309 ("industrial"/CEE), single phase. */
  'IEC_60309_1P',
  /** IEC 60309, three phase. */
  'IEC_60309_3P',
  /** IEC 60309 DC variant. */
  'IEC_60309_DC',
  /** IEC 62196 Type 1, a.k.a. SAE J1772. AC. */
  'TYPE1',
  /** IEC 62196 Type 2, a.k.a. Mennekes. AC. The Polish workhorse. */
  'TYPE2',
  /** IEC 62196 Type 3A (SCAME), AC. */
  'TYPE3A',
  /** IEC 62196 Type 3C, AC. */
  'TYPE3C',
  /** CCS Combo 1 (Type 1 based). DC. */
  'CCS1',
  /** CCS Combo 2 (Type 2 based). DC. The Polish DC workhorse. */
  'CCS2',
  /** CHAdeMO. DC. */
  'CHADEMO',
  /** Tesla proprietary. */
  'TESLA',
  /** China GB/T part 2, AC. */
  'GBT_AC',
  /** China GB/T part 3, DC. */
  'GBT_DC',
  /** Large paddle inductive. */
  'INDUCTIVE_LARGE',
  /** Small paddle inductive. */
  'INDUCTIVE_SMALL',
  /** Legacy Avcon. */
  'AVCON',
  /** Marechal industrial connector. */
  'MARECHAL',
  /** Legacy Better Place. */
  'BETTER_PLACE',
  /** Present in the data but not in any known dictionary row. */
  'UNKNOWN',
] as const

export const ConnectorStandard = ft.Union(
  ft.Literal('DOMESTIC'),
  ft.Literal('IEC_60309_1P'),
  ft.Literal('IEC_60309_3P'),
  ft.Literal('IEC_60309_DC'),
  ft.Literal('TYPE1'),
  ft.Literal('TYPE2'),
  ft.Literal('TYPE3A'),
  ft.Literal('TYPE3C'),
  ft.Literal('CCS1'),
  ft.Literal('CCS2'),
  ft.Literal('CHADEMO'),
  ft.Literal('TESLA'),
  ft.Literal('GBT_AC'),
  ft.Literal('GBT_DC'),
  ft.Literal('INDUCTIVE_LARGE'),
  ft.Literal('INDUCTIVE_SMALL'),
  ft.Literal('AVCON'),
  ft.Literal('MARECHAL'),
  ft.Literal('BETTER_PLACE'),
  ft.Literal('UNKNOWN'),
)
export type ConnectorStandard = ft.Static<typeof ConnectorStandard>

/**
 * EIPA `dictionary.connector_interface[].name` -> canonical standard.
 *
 * Verbatim from Tabela 3 of the UDT system description. Keys are matched after
 * {@link normalizeEipaConnectorName}, so casing and separator drift is tolerated.
 */
export const EIPA_CONNECTOR_NAME_TO_STANDARD: Readonly<Record<string, ConnectorStandard>> = {
  'DOMESTIC-A': 'DOMESTIC',
  'DOMESTIC-B': 'DOMESTIC',
  'DOMESTIC-C': 'DOMESTIC',
  'DOMESTIC-D': 'DOMESTIC',
  'DOMESTIC-E': 'DOMESTIC',
  'DOMESTIC-F': 'DOMESTIC',
  'DOMESTIC-E-F': 'DOMESTIC',
  'DOMESTIC-G': 'DOMESTIC',
  'DOMESTIC-H': 'DOMESTIC',
  'DOMESTIC-I': 'DOMESTIC',
  'DOMESTIC-J': 'DOMESTIC',
  'DOMESTIC-K': 'DOMESTIC',
  'DOMESTIC-L': 'DOMESTIC',
  'DOMESTIC-M': 'DOMESTIC',
  'NEMA-5-20': 'DOMESTIC',
  'IEC-309-2-1PH': 'IEC_60309_1P',
  'IEC-309-2-3PH': 'IEC_60309_3P',
  'IEC-309-2-DC': 'IEC_60309_DC',
  'IEC-62196-T1-M-CABLE': 'TYPE1',
  'IEC-62196-T1-F-NOCABLE': 'TYPE1',
  'IEC-62196-T2-F-CABLE': 'TYPE2',
  'IEC-62196-T2-F-NOCABLE': 'TYPE2',
  'IEC-62196-T3A-F': 'TYPE3A',
  'IEC-62196-T3C-F-NOCABLE': 'TYPE3C',
  'IEC-62196-T1-COMBO': 'CCS1',
  'IEC-62196-T2-COMBO': 'CCS2',
  CHADEMO: 'CHADEMO',
  'TESLA-SPECIFIC': 'TESLA',
  'CHINA-PART2': 'GBT_AC',
  'CHINA-PART3': 'GBT_DC',
  'LARGE-PADDLE': 'INDUCTIVE_LARGE',
  'SMALL-PADDLE': 'INDUCTIVE_SMALL',
  AVCON: 'AVCON',
  MARECHAL: 'MARECHAL',
  'BPLC-SPECIFIC': 'BETTER_PLACE',
}

/** Upper-cases and collapses separator/whitespace drift in a dictionary name. */
export const normalizeEipaConnectorName = (name: string): string =>
  name
    .trim()
    .toUpperCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-{2,}/g, '-')

/** Map one EIPA dictionary name onto a standard; `UNKNOWN` if unrecognised. */
export const connectorStandardFromEipaName = (name: string): ConnectorStandard =>
  EIPA_CONNECTOR_NAME_TO_STANDARD[normalizeEipaConnectorName(name)] ?? 'UNKNOWN'

/**
 * Build the id -> standard table for one snapshot of `dictionary.json`.
 *
 * Call this once per pipeline run with `dictionary.data.connector_interface`,
 * then resolve `point.connectors[].interfaces` through the result. Ids absent
 * from the dictionary resolve to `UNKNOWN` via {@link connectorStandardForId}.
 */
export const createConnectorStandardLookup = (
  entries: readonly { readonly id: number; readonly name: string }[],
): ReadonlyMap<number, ConnectorStandard> => {
  const lookup = new Map<number, ConnectorStandard>()
  entries.forEach((entry) => {
    lookup.set(entry.id, connectorStandardFromEipaName(entry.name))
  })
  return lookup
}

/** Resolve one dictionary id, defaulting to `UNKNOWN`. */
export const connectorStandardForId = (
  lookup: ReadonlyMap<number, ConnectorStandard>,
  id: number,
): ConnectorStandard => lookup.get(id) ?? 'UNKNOWN'

/**
 * Which current a standard carries, as a property of the plug itself.
 *
 * This is a lookup table, not an inference: `TYPE2` is an AC plug whatever
 * power the operator declares. Reconciling it with a contradictory declared
 * power is the job of `inferVoltage`.
 */
export const CONNECTOR_CURRENT_TYPE: Readonly<Record<ConnectorStandard, CurrentType>> = {
  DOMESTIC: 'AC',
  IEC_60309_1P: 'AC',
  IEC_60309_3P: 'AC',
  IEC_60309_DC: 'DC',
  TYPE1: 'AC',
  TYPE2: 'AC',
  TYPE3A: 'AC',
  TYPE3C: 'AC',
  CCS1: 'DC',
  CCS2: 'DC',
  CHADEMO: 'DC',
  // Tesla sells both an AC-only Type 2 style plug and a DC Supercharger plug.
  TESLA: 'unknown',
  GBT_AC: 'AC',
  GBT_DC: 'DC',
  INDUCTIVE_LARGE: 'unknown',
  INDUCTIVE_SMALL: 'unknown',
  AVCON: 'AC',
  MARECHAL: 'AC',
  BETTER_PLACE: 'AC',
  UNKNOWN: 'unknown',
}

export const connectorCurrentType = (standard: ConnectorStandard): CurrentType =>
  CONNECTOR_CURRENT_TYPE[standard]

/** Stable ordering for the `conn` array in published artifacts and for filter UIs. */
export const sortConnectorStandards = (
  standards: Iterable<ConnectorStandard>,
): ConnectorStandard[] => {
  const order = new Map(CONNECTOR_STANDARDS.map((standard, index) => [standard, index]))
  return [...new Set(standards)].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0))
}
